/*
 * <wishlist-page> (sections/main-wishlist.liquid): renders the saved products from
 * window.ObravenWishlist (assets/wishlist.js). Each card is fetched through the Section Rendering API
 * as /products/<handle>?section_id=wishlist-card; products that no longer exist (404) are removed from the list.
 * Re-renders on the wishlist:change event (and on changes from other tabs), so removing a heart
 * removes its card. Shows loading skeletons, an empty state and a live count.
 */
if (!customElements.get('wishlist-page')) {
  customElements.define(
    'wishlist-page',
    class WishlistPage extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.countElement = this.querySelector('[data-wishlist-page-count]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.heading = this.querySelector('[data-wishlist-heading]');
        this.skeleton = this.querySelector('[data-wishlist-skeleton]');
        this.root = this.dataset.rootUrl === '/' ? '' : (this.dataset.rootUrl || '').replace(/\/$/, '');
        this.missing = new Set();

        this.onChange = () => this.render();
        this.onStorage = (event) => {
          if (event.key === 'obraven:wishlist') this.render();
        };
        document.addEventListener('wishlist:change', this.onChange);
        window.addEventListener('storage', this.onStorage);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this.onChange);
        window.removeEventListener('storage', this.onStorage);
      }

      handles() {
        const list = window.ObravenWishlist ? window.ObravenWishlist.get() : [];
        return list.filter((handle) => !this.missing.has(handle));
      }

      render() {
        const handles = this.handles();
        const items = Array.from(this.grid.children);
        let focusIndex = -1;

        items.forEach((item, index) => {
          if (handles.includes(item.dataset.handle)) return;
          if (item.contains(document.activeElement)) focusIndex = index;
          item.remove();
        });

        const current = new Map(Array.from(this.grid.children).map((item) => [item.dataset.handle, item]));
        handles.forEach((handle, index) => {
          let item = current.get(handle);
          if (!item) {
            item = this.skeleton.content.firstElementChild.cloneNode(true);
            item.dataset.handle = handle;
            this.load(handle, item);
          }
          const at = this.grid.children[index];
          if (at !== item) this.grid.insertBefore(item, at || null);
        });

        this.update();

        // The focused card was removed: continue from the card that took its place.
        if (focusIndex >= 0) {
          const next = this.grid.children[Math.min(focusIndex, this.grid.children.length - 1)];
          const target = next ? next.querySelector('a[href]:not([tabindex="-1"])') : null;
          (target || this.heading).focus();
        }
      }

      async load(handle, item) {
        try {
          const response = await fetch(
            `${this.root}/products/${encodeURIComponent(handle)}?section_id=wishlist-card`
          );
          if (response.status === 404) throw new Error('gone');
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('.shopify-section');
          // Shopify answers 404 for unknown products, but some proxies return 200 with this marker.
          if (section?.querySelector('[data-wishlist-gone]')) throw new Error('gone');
          if (!section || !section.firstElementChild) throw new Error('missing');
          if (!item.isConnected) return;
          item.classList.remove('ob-wishlist__item--loading');
          item.removeAttribute('aria-hidden');
          item.replaceChildren(...Array.from(section.childNodes));
        } catch (error) {
          if (error.message === 'missing' || error.message === 'gone') this.missing.add(handle);
          item.remove();
          // A deleted or unpublished product can never come back into view: drop it from the saved
          // list so the header count stays right. Other failures (network, empty render) only skip it.
          if (error.message === 'gone') window.ObravenWishlist?.remove(handle);
        }
        this.update();
      }

      update() {
        const count = this.grid.children.length;
        const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
        const text = count > 0 && template ? template.replace('__COUNT__', String(count)) : '';
        if (this.countElement.textContent !== text) this.countElement.textContent = text;
        this.grid.hidden = count === 0;
        this.empty.hidden = count > 0;
      }
    }
  );
}
